import type { StyleSchema } from '@blocknote/core';
import {
  createReactInlineContentSpec,
  type ReactInlineContentImplementation,
} from '@blocknote/react';

export const chipClassName =
  'rounded-2xl bg-info-soft px-1 py-0.5 text-primary leading-5 break-all inline-block max-w-full';

const contextVariableConfig = {
  type: 'mention',
  propSchema: { contextVariable: { default: '' }, label: { default: '' } },
  content: 'none' as const,
};

const contextTokenConfig = {
  type: 'contextToken',
  propSchema: { token: { default: '' }, rawToken: { default: '' } },
  content: 'none' as const,
};

type ContextTokenConfig = typeof contextTokenConfig;

type ContextVariableTokenHighlightRenderProps = Parameters<
  ReactInlineContentImplementation<ContextTokenConfig, StyleSchema>['render']
>[0];

export const formatContextVariableDisplay = (value: string | undefined) => {
  if (!value) {
    return '{{}}';
  }

  const trimmed = value.trim();
  const match = trimmed.match(/^\{\{(.+)\}\}$/);
  const inner = match ? match[1] : trimmed;

  return `{{${inner}}}`;
};

// The ContextVariable inline content.
export const ContextVariable = createReactInlineContentSpec(contextVariableConfig, {
  render: ({ inlineContent }) => {
    const { contextVariable } = inlineContent.props;
    const displayValue = formatContextVariableDisplay(contextVariable);

    return <span className={chipClassName}>{displayValue}</span>;
  },
});

// Atomic so a single backspace deletes the whole chip and ProseMirror's joinBackward
// can merge the surrounding block when the cursor sits at its left edge.
export const ContextVariableTokenHighlightComponent = ({
  inlineContent,
}: ContextVariableTokenHighlightRenderProps) => {
  const fallbackToken = inlineContent.props.rawToken || inlineContent.props.token;
  const displayValue = formatContextVariableDisplay(fallbackToken);

  return <span className={chipClassName}>{displayValue}</span>;
};

export const ContextVariableTokenHighlight = createReactInlineContentSpec(contextTokenConfig, {
  render: ContextVariableTokenHighlightComponent,
});
